import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Input, Tag, Flex, theme, Card, Button } from 'antd';
import { TeamOutlined, SearchOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useClientCustomersQuery } from '@/api/auth/queries';
import { CustomersTable, filterCustomers } from '@/components/customers/CustomersTable';
import { useEffectiveClientId } from '@/contexts/AdminClientsContext';
import { createManageRouteGuard } from '@/utils/manageRouteGuards';
import { TENANT_ROLES } from '@/constants/tenantRoles';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { FilterBar } from '@/components/ui/FilterBar';
import { useCustomerDeletionPreview, useDeleteCustomersMutation } from '@/api/mcp-crm/mutations';
import { BulkActionBar, DeleteEntityModal, useBulkDeletion } from '@/components/shared';
import { useAuth } from '@/hooks/useAuth';
import { isAdminEquivalentRole } from '@/utils/authz';
import { useSelectionPrune } from '@/hooks/useSelectionPrune';

export const Route = createFileRoute('/manage/customers')({
  beforeLoad: createManageRouteGuard({
    requiredTenantRoles: [TENANT_ROLES.OWNER, TENANT_ROLES.EMPLOYEE]
  }),
  component: ManageCustomersPage
});

function ManageCustomersPage() {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  const clientId = useEffectiveClientId();
  const [search, setSearch] = useState('');

  const customersQuery = useClientCustomersQuery(clientId ?? '', !!clientId);
  const users = customersQuery.data?.users ?? [];
  const isLoading = customersQuery.isLoading;
  const isFetching = customersQuery.isFetching;
  const hasActiveFilters = search.trim().length > 0;

  const filtered = useMemo(() => filterCustomers(users, search), [users, search]);

  // Admin-only (ADMIN / DEVELOPER) bulk delete, keyed by auth user id
  const { payload } = useAuth();
  const canDelete = isAdminEquivalentRole(payload?.role);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const loadPreview = useCustomerDeletionPreview();
  const deleteCustomersMutation = useDeleteCustomersMutation();
  // Keep only failed customers selected so they can be retried.
  const customerDeletion = useBulkDeletion(setSelectedIds);

  // Bulk actions only apply to rows the user can see.
  const visibleIds = useMemo(() => filtered.map((row) => row.id), [filtered]);
  useSelectionPrune(visibleIds, setSelectedIds);

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader icon={<TeamOutlined />} title={t('customers.title')} description={t('customers.description')} />

      <FilterBar
        actions={
          <>
            {hasActiveFilters && (
              <Tag color="blue" style={{ margin: 0 }}>
                {t('customers.filters.customersCount', { filtered: filtered.length, total: users.length })}
              </Tag>
            )}
            <Button
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={() => void customersQuery.refetch()}
              disabled={isFetching}
            >
              {t('customers.filters.refresh')}
            </Button>
          </>
        }
      >
        <Input
          placeholder={t('customers.search')}
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ flex: '1 1 220px', minWidth: 160, maxWidth: 420 }}
        />
      </FilterBar>

      <Card
        title={
          <Flex align="center" gap={token.marginXS}>
            <span>{t('customers.table.title')}</span>
            {!isLoading && (
              <Tag color={hasActiveFilters ? 'blue' : 'default'} style={{ margin: 0 }}>
                {filtered.length}
              </Tag>
            )}
          </Flex>
        }
        styles={{ body: { padding: 0 } }}
      >
        {selectedIds.length > 0 && (
          <div style={{ padding: `${token.paddingSM}px ${token.paddingSM}px 0` }}>
            <BulkActionBar selectedCount={selectedIds.length} onClearSelection={() => setSelectedIds([])}>
              <Button danger icon={<DeleteOutlined />} onClick={() => customerDeletion.request(selectedIds)}>
                {t('deleteEntity.deleteSelected', { count: selectedIds.length })}
              </Button>
            </BulkActionBar>
          </div>
        )}
        <CustomersTable
          users={filtered}
          loading={isLoading}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={() => setSearch('')}
          selectedIds={selectedIds}
          onSelectionChange={canDelete ? setSelectedIds : undefined}
          onDelete={canDelete ? customerDeletion.request : undefined}
        />
      </Card>

      <DeleteEntityModal
        entity="customers"
        {...customerDeletion.modalState}
        loadPreview={(userIds) => loadPreview(userIds.map((userId) => ({ userId })))}
        onConfirm={(userIds) => deleteCustomersMutation.mutateAsync(userIds.map((userId) => ({ userId })))}
        isDeleting={deleteCustomersMutation.isPending}
      />
    </PageTransition>
  );
}
